import React from "react";
import { ModeRendererProps } from "./types";

export function VasarelyEscherMode({ brand }: ModeRendererProps) {
  // Recursive perception and self-referential logic
  // Tessellations, impossible spaces, loops
  
  return (
    <g>
      <rect x="0" y="0" width="794" height="1123" fill={brand.paper} />
      
      {/* Penrose triangle - impossible object */}
      <g transform="translate(250, 200)">
        <path
          d="M 0,-80 L 70,40 L -70,40 Z"
          fill="none"
          stroke={brand.deep}
          strokeWidth="16"
          strokeLinejoin="miter"
          opacity={0.9}
        />
        <path
          d="M -5,-75 L -65,35 L 65,35 Z"
          fill={brand.aqua}
          opacity={0.3}
        />
      </g>
      
      {/* Impossible staircase - Penrose stairs */}
      {Array.from({ length: 8 }).map((_, i) => {
        const angle = (i / 8) * Math.PI * 2;
        const cx = 550;
        const cy = 220;
        const r = 80;
        const x = cx + Math.cos(angle) * r;
        const y = cy + Math.sin(angle) * r;
        const width = 60;
        const height = 20;
        
        return (
          <g key={`stair-${i}`}>
            <rect
              x={x - width / 2}
              y={y - height / 2}
              width={width}
              height={height}
              fill={i % 2 === 0 ? brand.violet : brand.aqua}
              opacity={0.7}
            />
            <line
              x1={x - width / 2}
              y1={y}
              x2={x + width / 2}
              y2={y}
              stroke={brand.deep}
              strokeWidth="2"
            />
          </g>
        );
      })}
      
      {/* Tessellation pattern - fish/bird-like */}
      {Array.from({ length: 6 }).map((_, row) =>
        Array.from({ length: 8 }).map((_, col) => {
          const x = 80 + col * 80;
          const y = 420 + row * 70;
          const isEven = (row + col) % 2 === 0;
          
          // Simple fish-like tessellation
          if (isEven) {
            return (
              <g key={`tess-${row}-${col}`}>
                <ellipse
                  cx={x + 40}
                  cy={y + 35}
                  rx="35"
                  ry="20"
                  fill={brand.aqua}
                  opacity={0.6}
                />
                <polygon
                  points={`${x + 75},${y + 35} ${x + 85},${y + 25} ${x + 85},${y + 45}`}
                  fill={brand.aqua}
                  opacity={0.6}
                />
                <circle cx={x + 20} cy={y + 30} r="3" fill={brand.deep} opacity={0.8} />
              </g>
            );
          } else {
            return (
              <g key={`tess-${row}-${col}`}>
                <ellipse
                  cx={x + 40}
                  cy={y + 35}
                  rx="35"
                  ry="20"
                  fill={brand.violet}
                  opacity={0.6}
                  transform={`rotate(180 ${x + 40} ${y + 35})`}
                />
                <polygon
                  points={`${x + 5},${y + 35} ${x - 5},${y + 25} ${x - 5},${y + 45}`}
                  fill={brand.violet}
                  opacity={0.6}
                />
                <circle cx={x + 60} cy={y + 40} r="3" fill={brand.deep} opacity={0.8} />
              </g>
            );
          }
        })
      )}
      
      {/* Infinite loop - Möbius strip representation */}
      {Array.from({ length: 40 }).map((_, i) => {
        const angle = (i / 40) * Math.PI * 2;
        const twist = (i / 40) * Math.PI;
        const cx = 400;
        const cy = 900;
        const r1 = 140;
        const r2 = 100;
        
        const x1 = cx + Math.cos(angle) * r1 + Math.cos(twist) * 20;
        const y1 = cy + Math.sin(angle) * r1 * 0.5 + Math.sin(twist) * 10;
        const x2 = cx + Math.cos(angle) * r2 + Math.cos(twist + Math.PI) * 20;
        const y2 = cy + Math.sin(angle) * r2 * 0.5 + Math.sin(twist + Math.PI) * 10;
        
        return (
          <line
            key={`mobius-${i}`}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={brand.amber}
            strokeWidth="1.5"
            opacity={0.5}
          />
        );
      })}
      
      {/* Vasarely optical recursion */}
      <g transform="translate(150, 850)">
        {Array.from({ length: 8 }).map((_, i) => {
          const size = 140 - i * 16;
          const offset = i * 8;
          const rotation = i * 5;
          
          return (
            <rect
              key={`recursive-${i}`}
              x={offset}
              y={offset}
              width={size}
              height={size}
              fill="none"
              stroke={i % 2 === 0 ? brand.violet : brand.aqua}
              strokeWidth="2"
              opacity={0.7 - i * 0.07}
              transform={`rotate(${rotation} ${70} ${70})`}
            />
          );
        })}
      </g>
      
      {/* Self-referential pattern - chase diagram */}
      {Array.from({ length: 12 }).map((_, i) => {
        const angle = (i / 12) * Math.PI * 2;
        const cx = 600;
        const cy = 900;
        const r = 100;
        const x1 = cx + Math.cos(angle) * r;
        const y1 = cy + Math.sin(angle) * r;
        const x2 = cx + Math.cos(angle + Math.PI * 0.7) * r;
        const y2 = cy + Math.sin(angle + Math.PI * 0.7) * r;
        
        return (
          <g key={`chase-${i}`}>
            <line
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={brand.violet}
              strokeWidth="2"
              opacity={0.6}
            />
            <circle cx={x1} cy={y1} r="5" fill={brand.aqua} opacity={0.8} />
          </g>
        );
      })}
      
      {/* Impossible cube overlay */}
      <g transform="translate(250, 1000)">
        {/* Necker cube - ambiguous depth */}
        <path
          d="M 0,0 L 60,0 L 60,60 L 0,60 Z"
          fill="none"
          stroke={brand.deep}
          strokeWidth="2"
        />
        <path
          d="M 20,-20 L 80,-20 L 80,40 L 20,40 Z"
          fill="none"
          stroke={brand.deep}
          strokeWidth="2"
        />
        <line x1="0" y1="0" x2="20" y2="-20" stroke={brand.deep} strokeWidth="2" />
        <line x1="60" y1="0" x2="80" y2="-20" stroke={brand.deep} strokeWidth="2" />
        <line x1="60" y1="60" x2="80" y2="40" stroke={brand.deep} strokeWidth="2" />
        <line x1="0" y1="60" x2="20" y2="40" stroke={brand.deep} strokeWidth="2" />
      </g>
    </g>
  );
}
